import { Environment } from "@omni/types/enums/environment.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { values } from "@omni/utils/object.utils";
import { createContext, ReactNode, use, useEffect, useState } from "react";

import FeatureTogglesService from "@/services/featureToggle/featureToggles.service";
import { WindowEvent } from "@/services/windowEvents/windowEvent.service";
import { Config } from "@/utils/config.utils";

const defaultFeatureToggles: Record<FeatureToggleKey, boolean> = {
    [FeatureToggleKey.DEBUG_ANALYTICS_LOGGING]: false,
    [FeatureToggleKey.DEBUG_EVENT_DATA_LAYER_LOGGING]: false,
    [FeatureToggleKey.DEBUG_ALGOLIA]: false,
    [FeatureToggleKey.DEBUG_FEATURE_SWITCH_UI]: false,
    [FeatureToggleKey.FEATURE_CART_ITEM_PROMOTION_LABEL]: false,
    [FeatureToggleKey.FEATURE_WISHLIST_BADGE]: false,
    [FeatureToggleKey.FEATURE_AUTHORISATION_MODE]: false,
    [FeatureToggleKey.FEATURE_STICKY_ECOMMERCE]: false,
    [FeatureToggleKey.FEATURE_NEVERBOUNCE]: false,
    [FeatureToggleKey.FEATURE_REVIEWS]: false,
    [FeatureToggleKey.FEATURE_CONTACTFORM_ATTACHMENTS]: false,
    [FeatureToggleKey.FEATURE_KLARNA_OSM]: false,
    [FeatureToggleKey.FEATURE_WARRANTY_CONFORMANCE]: false
};

export type FeatureToggleContextValues = Record<FeatureToggleKey, boolean>;

export type FeatureToggleContextProviderProps = {
    serverSideFeatureToggles?: Partial<Record<FeatureToggleKey, boolean>>;
    children: ReactNode;
};

export const FeatureToggleContext = createContext<FeatureToggleContextValues>(
    {} as FeatureToggleContextValues
);
FeatureToggleContext.displayName = "FeatureToggleContext";

export const FeatureToggleContextProvider = ({
    serverSideFeatureToggles,
    children
}: FeatureToggleContextProviderProps) => {
    const [featureToggles, setFeatureToggles] = useState<
        Record<FeatureToggleKey, boolean>
    >({
        ...defaultFeatureToggles,
        ...values(FeatureToggleKey).reduce<
            Partial<Record<FeatureToggleKey, boolean>>
        >((acc, key) => {
            const featureEnabled = FeatureTogglesService.isEnabled(key, {
                serverFeatureToggles: serverSideFeatureToggles
            });

            if (isDefined(featureEnabled)) {
                acc[key] = featureEnabled;
            }

            return acc;
        }, {})
    });

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            WindowEvent.FeatureToggleChange,
            (event) => {
                setFeatureToggles((prev) => ({
                    ...prev,
                    [event.detail.key]: event.detail.enabled
                }));
            },
            {
                signal: abortController.signal
            }
        );

        return () => abortController.abort();
    }, []);

    return (
        <FeatureToggleContext value={featureToggles}>
            {children}
        </FeatureToggleContext>
    );
};

export const useFeatureToggleContext = () => {
    const context = use<FeatureToggleContextValues>(FeatureToggleContext);

    if (!isDefined(context)) {
        throw new Error(`${FeatureToggleContext.displayName} is undefined.`);
    }

    return context;
};

export const useFeatureToggle = (
    key: FeatureToggleKey,
    envOverrides: Array<Environment> = []
) => {
    const context = useFeatureToggleContext();

    if (envOverrides.some((e) => e === Config.general.environment)) {
        return true;
    } else {
        return context[key] ?? false;
    }
};
